import { useMemo } from 'react';
import { startOfMonth, startOfWeek, endOfWeek, isAfter, isBefore, subDays } from 'date-fns';

export default function useDashboardStats({ clients, bookings, payments }) {
  return useMemo(() => {
    const now = new Date();
    const mtdStart = startOfMonth(now);
    const todayStr = now.toISOString().slice(0, 10);
    const lapsedThreshold = subDays(now, 60);
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

    const toDate = (val) => {
      if (!val) return null;
      if (val.toDate) return val.toDate();
      return new Date(val);
    };

    // MTD Revenue from completed bookings this month
    const mtdFromBookings = bookings
      .filter(b => {
        if (b.status !== 'completed') return false;
        const d = toDate(b.scheduled_at || b.created_at);
        if (!d) return false;
        return isAfter(d, mtdStart) || d.toISOString().slice(0, 10) === mtdStart.toISOString().slice(0, 10);
      })
      .reduce((sum, b) => sum + (Number(b.price) || 0), 0);

    const mtdFromPayments = payments
      .filter(p => {
        const d = toDate(p.created_at);
        if (!d) return false;
        return isAfter(d, mtdStart);
      })
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    const mtdRevenue = mtdFromBookings > 0 ? mtdFromBookings : mtdFromPayments;

    // Jobs this calendar week
    const weekBookings = bookings.filter(b => {
      const d = toDate(b.scheduled_at);
      if (!d) return false;
      return isAfter(d, weekStart) && isBefore(d, weekEnd);
    }).length;

    // Today's jobs
    const todayJobs = bookings.filter(b => {
      const d = toDate(b.scheduled_at);
      if (!d) return false;
      return d.toISOString().slice(0, 10) === todayStr;
    });

    // Active maintenance slots
    const maintenanceSlots = clients.filter(c => c.type === 'maintenance' && c.status === 'active').length;

    // Outstanding balance (pending payments)
    const outstanding = payments
      .filter(p => p.status === 'pending')
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    // Lapsed clients
    const lapsedClients = clients.filter(c => {
      const d = toDate(c.last_service_date);
      if (!d) return false;
      return d < lapsedThreshold;
    });

    // Upcoming jobs (next 7 days)
    const nextWeek = subDays(now, -7);
    const upcomingJobs = bookings
      .filter(b => {
        const d = toDate(b.scheduled_at);
        if (!d) return false;
        return isAfter(d, now) && isBefore(d, nextWeek) && b.status !== 'cancelled' && b.status !== 'completed';
      })
      .sort((a, b) => toDate(a.scheduled_at) - toDate(b.scheduled_at));

    return { mtdRevenue, weekBookings, maintenanceSlots, outstanding, todayJobs, lapsedClients, upcomingJobs };
  }, [clients, bookings, payments]);
}
